import React, { Component } from "react";
import { withRouter } from "react-router-dom";
import { withStyles } from "@material-ui/core/styles";

import styles from "./teamPhotosStyles";
import TeamPhotosView from "./teamPhotosView";
import "./teamPhotos.css";
import ApiTour from "../../../API/ApiTour";
import ApiGameAnalytics from "../../../API/ApiGameAnalytics";
import { POLL_INTERVAL_MS } from "./sessionUtils";

class TeamPhotosComponent extends Component {
  constructor(props) {
    super(props);
    this.state = {
      showError: false,
      errorMessage: "",
      showSuccess: false,
      successMessage: "",
      sessions: [],
      tour: null,
      tourId: null,
      page: 0,
      rowPerPage: 10,
      totalRows: 0,
      sortColumn: "created_at",
      loading: true,
      refreshing: false,
      lastRefreshedAt: null,
    };
    this.pollTimer = null;
  }

  componentDidMount() {
    document.title = "Sessions | Landing Zone";
    this.getTourIdFromQuery();
    document.addEventListener("visibilitychange", this.handleVisibilityChange);
  }

  componentWillUnmount() {
    this.stopPolling();
    document.removeEventListener(
      "visibilitychange",
      this.handleVisibilityChange
    );
  }

  getTourIdFromQuery = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const tourId = urlParams.get("tourId");

    if (tourId) {
      this.setState({ tourId: parseInt(tourId) }, () => {
        this.getTour();
        this.getSessions();
        this.startPolling();
      });
    } else {
      this.setState({
        showError: true,
        errorMessage: "ID du tour manquant",
        loading: false,
      });
    }
  };

  startPolling = () => {
    if (this.pollTimer) return;
    this.pollTimer = setInterval(() => {
      // Inutile d'interroger l'API sur un onglet en arrière-plan : le
      // rafraîchissement reprend au retour de l'utilisateur.
      if (document.visibilityState === "hidden") return;
      this.getSessions({ silent: true });
    }, POLL_INTERVAL_MS);
  };

  stopPolling = () => {
    if (!this.pollTimer) return;
    clearInterval(this.pollTimer);
    this.pollTimer = null;
  };

  handleVisibilityChange = () => {
    if (document.visibilityState === "visible" && this.state.tourId) {
      this.getSessions({ silent: true });
    }
  };

  getTour = async () => {
    try {
      const response = await ApiTour.getTour(this.state.tourId);
      if (response.status === 200) {
        this.setState({ tour: response.data });
      } else {
        console.error(
          "Erreur lors de la récupération du tour:",
          response.status
        );
      }
    } catch (error) {
      console.error("Erreur lors de la récupération du tour:", error);
    }
  };

  // `silent` : rafraîchissement de fond (polling, retour d'onglet). On ne
  // remplace pas la vue par un loader, sinon le tableau clignote toutes les
  // 20 s et le détail ouvert se ferme.
  getSessions = async ({ silent = false } = {}) => {
    if (!this.state.tourId) return;

    try {
      this.setState(silent ? { refreshing: true } : { loading: true });
      const response = await ApiGameAnalytics.getSessions(
        this.state.tourId,
        this.state.page + 1,
        this.state.rowPerPage,
        this.state.sortColumn
      );

      if (response.status === 200) {
        this.setState({
          sessions: response.data.items,
          totalRows: response.data.total,
          loading: false,
          refreshing: false,
          lastRefreshedAt: new Date(),
          // Un rafraîchissement réussi efface une erreur réseau passagère.
          showError: false,
          errorMessage: "",
        });
      } else {
        throw new Error("Erreur lors de la récupération des sessions");
      }
    } catch (error) {
      console.error("Erreur:", error);
      // Une erreur en fond ne doit pas faire disparaître les données déjà
      // affichées : on garde le tableau et on laisse le polling retenter.
      if (silent) {
        this.setState({ refreshing: false });
        return;
      }
      this.setState({
        showError: true,
        errorMessage: error.message,
        loading: false,
      });
    }
  };

  handleRefreshNow = () => {
    this.getSessions({ silent: true });
  };

  handleChangePage = (event, newPage) => {
    this.setState({ page: newPage }, () => {
      this.getSessions();
    });
  };

  handleChangeRowsPerPage = (event) => {
    this.setState(
      {
        rowPerPage: parseInt(event.target.value),
        page: 0,
      },
      () => {
        this.getSessions();
      }
    );
  };

  setSortColumn = (column) => {
    this.setState(
      {
        sortColumn: column,
        page: 0,
      },
      () => {
        this.getSessions();
      }
    );
  };

  deleteTeamPhoto = async (teamPhotoId) => {
    if (!teamPhotoId) return { success: false };

    try {
      const response = await ApiGameAnalytics.deleteTeamPhoto(teamPhotoId);

      if (response.status === 200 || response.status === 204) {
        this.setState((prevState) => ({
          sessions: (prevState.sessions || []).map((session) => ({
            ...session,
            teamPhotos: (session.teamPhotos || []).filter(
              (teamPhoto) => teamPhoto.id !== teamPhotoId
            ),
          })),
        }));
        return { success: true };
      }

      this.setState({
        showError: true,
        errorMessage: "Erreur lors de la suppression de la photo",
      });
      return { success: false };
    } catch (error) {
      this.setState({
        showError: true,
        errorMessage: "Erreur lors de la suppression de la photo",
      });
      return { success: false };
    }
  };

  formatDate = (date) => {
    if (!date) return "";
    return new Date(date).toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  render() {
    const { classes } = this.props;

    return (
      <TeamPhotosView
        {...this.props}
        state={this.state}
        classes={classes}
        onPageChange={this.handleChangePage}
        onRowsPerPageChange={this.handleChangeRowsPerPage}
        onSortColumn={this.setSortColumn}
        formatDate={this.formatDate}
        onDeleteTeamPhoto={this.deleteTeamPhoto}
        onRefreshNow={this.handleRefreshNow}
      />
    );
  }
}

export default withStyles(styles, { withTheme: true })(
  withRouter(TeamPhotosComponent)
);
